import React, { useState, useEffect } from 'react';
import { Modal, Box, Button } from '@mui/material';

import type { ExamDTO } from '../models/examDTOs';
import { apiRequest } from '@/src/services';
import { Loading } from '@/src/ui';

interface PublishModalProps {
    resourceId: number | undefined;
    handleClose: (_shouldRefresh: boolean, _isInTimeout: boolean) => void;
}

const PublishModal: React.FC<PublishModalProps> = ({ resourceId, handleClose }) => {
    const [hasLoaded, setHasLoaded] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState(false);

    const [data, setData] = useState<ExamDTO | null>(null);

    useEffect(() => {
        (async function init() {
            let res = null;
            try {
                res = await apiRequest<ExamDTO>('GET', `assessments/exams/noXml/${resourceId}`);
            } catch {
                setError('Failed to load exam data');
                return;
            } finally {
                setHasLoaded(true);
            }

            setData(res.data);
        })();
    }, [resourceId]);

    const handleSubmit = async (e: React.FormEvent<any>) => {
        e.preventDefault();

        if (submitting || success) return;

        if (!data?.id) setError('Unable to load exam Id');

        setSubmitting(true);
        setError('');
        try {
            await apiRequest('PATCH', `assessments/exams/publish/${data?.id}`, {
                isPublished: !data?.isPublished,
            });

            setTimeout(() => {
                handleClose(true, false);
                setSuccess(false);
            }, 750);
            setSuccess(true);
        } catch {
            setError('Failed to publish exam');
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Modal open={true} onClose={() => handleClose(false, success)}>
            <Box sx={{ p: 4, bgcolor: 'background.paper', margin: 'auto', mt: 8, width: 400 }}>
                <h2>{data && data.isPublished ? 'Unpublish' : 'Publish'} Exam</h2>
                {!hasLoaded ? <Loading message="Loading..." size="lg" /> : renderExamMessage(data)}
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 4 }}>
                    <Button variant="outlined" onClick={() => handleClose(false, false)}>
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        color={data && data.isPublished ? 'error' : 'success'}
                        onClick={handleSubmit}
                    >
                        {data && data.isPublished ? 'Unpublish' : 'Publish'}
                    </Button>
                </Box>
                {error && <div style={{ color: 'red', marginTop: 8 }}>{error}</div>}
                {success && (
                    <div style={{ color: 'green', marginTop: 8 }}>Exam updated successfully!</div>
                )}
                {submitting && <Loading message="Processing..." size="sm" />}
            </Box>
        </Modal>
    );
};

function renderExamMessage(exam: ExamDTO | null) {
    if (!exam) {
        return <p>No exam selected.</p>;
    } else {
        return (
            <>
                <p>
                    Are you sure you want to {exam.isPublished ? 'deactivate' : 'publish'} this
                    exam?
                </p>
                <p>
                    <strong>Name: {exam.title}</strong>
                </p>
            </>
        );
    }
}

export default PublishModal;
